// Colonization: a territory revealed from the current events is auctioned. In turn order from the player who revealed
// it, each player bids more than the bid so far or passes (for good); no one is passed for them, since that would tell
// the others they hold no colonization cards. A bid may not be more than the bidder could pay. When everyone else has
// passed, the highest bidder pays by sacrificing military units, then takes the territory as a colony. With no bids,
// the territory leaves the game.
//
// Paying: the sacrificed units count as if they were the player's whole military: their own strength plus what they
// form for the current tactic (air forces doubling armies as usual); then the colonization bonus (special
// technologies, wonders, leaders) and colonization bonus cards played from the hand (James Cook: up to 2 other
// military cards, +1 each). At least one unit must go. Paying more than the bid is allowed.
//
// The auction is kept in the game state: { territory, order, passed, highest: { player, bid } | null, bids }, bids being
// every bid and pass so far, in order ({ player, bid } or { player, pass: true }), for everyone to follow.
const CardConverter = require('../Utilities/Converters/CardConverter');
const CardToData = require('../Utilities/Converters/CardToData');
const TacticConverter = require('../Utilities/Converters/TacticConverter');
const { CardRef } = require('../Utilities/CardRefs');
const { TacticArmies } = require('../Effects/Tactics');
const { Rules, HasRuleException, FireTriggers, TriggerEvents } = require('../Effects/Effects');
const { MilitaryCardTypes, CivilCardTypes } = require('../Constants/Names/CardTypes');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const LeaderNames = require('../Constants/Names/LeaderNames');
const BonusCard = require('../entities/Cards/BonusCard');
const { RequestDecision } = require('./Decisions');

const MilitaryTypes = Object.values(TechnologyTypes.Military);

// ---------------- Paying for a colony

// What sacrificing these units ({ cardName: count }) is worth: their strength as if they were the player's only units
const SacrificeStrength = (player, units) => {
    const sacrificed = Object.entries(units ?? {})
        .filter(([, count]) => count > 0)
        .map(([name, count]) => {
            const tech = player.GetTechnology(name);
            return { name, type: tech.type, age: tech.age, multiplier: tech.multiplier, GetWorkerCount: () => count };
        });
    const own = sacrificed.reduce((sum, unit) => sum + unit.multiplier * unit.GetWorkerCount(), 0);
    const tactic = player.currentTactic && TacticConverter(player.currentTactic);
    // (Marlene Dietrich's unit counted twice: one of those sent)
    const armies = tactic ? TacticArmies({ technologies: sacrificed }, tactic, { infantryAsCavalry: HasRuleException(player, Rules.InfantryAsCavalry),
        doubleOne: HasRuleException(player, Rules.DoubleOneUnit) }) : null;
    // (each army is worth more on a tactic card Sun Tzu went under, as in the player's strength)
    return own + (armies ? armies.value + player.TacticBonus() * (armies.full + armies.obsolete) : 0);
};

const IsBonusCard = (card) => card.type === MilitaryCardTypes.Bonus;
const HasJamesCook = (player) => player.leader?.name === LeaderNames.JamesCook;
// Isabella of Castile: resources paid (1, 3 or 6) for colonization points (+1, +2 or +3)
const IsabellaPoints = { 1: 1, 3: 2, 6: 3 };
const HasIsabella = (player) => player.leader?.name === LeaderNames.IsabellaOfCastile;

// A card as data for a recap to draw (and hover): its own data, or for one on a player's board (a technology) or a
// tactic, its type, name and age
const CardData = (card) => {
    if (!card?.name)
    {
        return null;
    }
    return CardToData(card) ?? { type: card.Technology || card.scienceCost !== undefined ? CivilCardTypes.Technology : card.type, name: card.name, age: card.age };
};

// How a payment adds up, for everyone to see, in two steps:
//   sacrificed - what was given up (kind: 'units', 'tactic' or 'cards'): units by kind ({ count, card, value }), the armies they formed for the current
//                tactic ({ card: the tactic, value }), colonization cards played ({ count, card, value }) and James
//                Cook's cards (by age, face down: { count, back: age, value }); sacrificeTotal their sum
//   bonuses    - what the player's cards add on top ({ card, label, value }, e.g. Cartography 2); total the payment
// Each value is for the whole part (e.g. 2 x Warrior: 2). text: both lines, e.g.
// "2 x Warrior + 1 x Age I Defense/Colonization = 2 + 1 = 3; 3 + Cartography = 3 + 2 = 5"
const PaymentBreakdown = (player, payment) => {
    const sacrificed = [];
    Object.entries(payment.units ?? {}).filter(([, count]) => count > 0).forEach(([name, count]) => {
        const tech = player.GetTechnology(name);
        sacrificed.push({ kind: 'units', count, label: name, card: CardData(tech), value: tech.multiplier * count });
    });
    const units = sacrificed.reduce((sum, part) => sum + part.value, 0);
    const armies = SacrificeStrength(player, payment.units) - units;
    if (armies > 0)
    {
        const tactic = TacticConverter(player.currentTactic);
        sacrificed.push({ kind: 'tactic', label: tactic.name, card: CardData(tactic), value: armies });
    }
    // Cards alike (the same age) together
    const grouped = (cards, part) => {
        const byAge = new Map();
        cards.forEach(card => byAge.set(card.age, [...(byAge.get(card.age) ?? []), card]));
        [...byAge.values()].forEach(alike => sacrificed.push({ kind: 'cards', count: alike.length, ...part(alike[0]), value: alike.length * part(alike[0]).each }));
    };
    grouped(player.militaryHand.filter(card => (payment.bonusCards ?? []).includes(card.id)),
        card => ({ label: `${card.age} ${card.name}`, card: CardData(card), each: BonusCard.GetColonizationBonus(card.age) }));
    grouped(player.militaryHand.filter(card => (payment.cookCards ?? []).includes(card.id)),
        card => ({ label: `${card.age} card (James Cook)`, back: card.age, each: 1 }));
    sacrificed.forEach(part => delete part.each);
    const sacrificeTotal = sacrificed.reduce((sum, part) => sum + part.value, 0);

    const byCard = new Map();
    player.colonizationBonusCalculationDelegate.forEach(delegate => {
        const card = delegate.Through ?? delegate.Source;
        const name = card?.name ?? 'Unknown';
        const entry = byCard.get(name) ?? { label: name, card: CardData(card), value: 0 };
        entry.value += delegate.Action();
        byCard.set(name, entry);
    });
    const bonuses = [...byCard.values()].filter(entry => entry.value !== 0);
    if (IsabellaPoints[payment.isabella])
    {
        bonuses.push({ label: `${player.leader.name} (${payment.isabella} resources)`, card: CardData(player.leader), value: IsabellaPoints[payment.isabella] });
    }
    const total = PaymentTotal(player, payment);

    const sum = (start, parts) => [...start, ...parts.map(part => part.value)].join(' + ');
    const first = `${sacrificed.map(part => part.count ? `${part.count} x ${part.label}` : part.label).join(' + ')} = ${sum([], sacrificed)} = ${sacrificeTotal}`;
    const second = bonuses.length > 0 ? `; ${sacrificeTotal} + ${bonuses.map(part => part.label).join(' + ')} = ${sum([sacrificeTotal], bonuses)} = ${total}` : '';
    return { sacrificed, sacrificeTotal, bonuses, total, text: first + second };
};

// What a payment is worth: { units: { cardName: count }, bonusCards: [ids], cookCards: [ids], isabella: resources paid }
const PaymentTotal = (player, payment) => SacrificeStrength(player, payment.units)
    + (IsabellaPoints[payment.isabella] ?? 0)
    + player.GetColonizationBonus()
    + player.militaryHand.filter(card => (payment.bonusCards ?? []).includes(card.id)).reduce((sum, card) => sum + BonusCard.GetColonizationBonus(card.age), 0)
    + (payment.cookCards ?? []).length;

// Why a payment is not allowed, or null
const PaymentProblem = (player, payment, bid) => {
    const units = Object.entries(payment?.units ?? {}).filter(([, count]) => count > 0);
    if (units.length === 0)
    {
        return "At least one military unit has to be sacrificed";
    }
    for (const [name, count] of units)
    {
        const tech = player.GetTechnology(name);
        if (!tech || !MilitaryTypes.includes(tech.type) || !Number.isInteger(count) || count > tech.GetWorkerCount())
        {
            return `You do not have ${count} ${name} to sacrifice`;
        }
    }
    const hand = player.militaryHand;
    if ((payment.bonusCards ?? []).some(id => !hand.some(card => card.id === id && IsBonusCard(card))))
    {
        return "Only your colonization bonus cards can be played";
    }
    const cook = payment.cookCards ?? [];
    if (cook.length > 0 && (!HasJamesCook(player) || cook.length > 2
        || cook.some(id => !hand.some(card => card.id === id && !IsBonusCard(card)) || (payment.bonusCards ?? []).includes(id))))
    {
        return "James Cook lets you discard up to 2 other military cards";
    }
    if (payment.isabella !== undefined && payment.isabella !== 0)
    {
        if (!HasIsabella(player) || !IsabellaPoints[payment.isabella])
        {
            return "Isabella of Castile lets you pay 1, 3 or 6 resources for 1, 2 or 3 colonization points";
        }
        if (player.GetResources() < payment.isabella)
        {
            return `You do not have ${payment.isabella} resources`;
        }
    }
    const total = PaymentTotal(player, payment);
    return total < bid ? `That is worth ${total}; the bid is ${bid}` : null;
};

// The most the player could pay: every unit, every colonization card, and James Cook's 2 cards
const MostPayable = (player) => {
    const units = Object.fromEntries(player.technologies.filter(tech => MilitaryTypes.includes(tech.type) && tech.GetWorkerCount() > 0)
        .map(tech => [tech.name, tech.GetWorkerCount()]));
    if (Object.keys(units).length === 0)
    {
        return 0;
    }
    const bonusCards = player.militaryHand.filter(IsBonusCard).map(card => card.id);
    const cookCards = HasJamesCook(player) ? player.militaryHand.filter(card => !IsBonusCard(card)).slice(0, 2).map(card => card.id) : [];
    const isabella = HasIsabella(player) ? Math.max(0, ...Object.keys(IsabellaPoints).map(Number).filter(amount => amount <= player.GetResources())) : 0;
    return PaymentTotal(player, { units, bonusCards, cookCards, isabella });
};

// ---------------- The auction

// Starts the auction for a revealed territory, the one who revealed it bidding first
const StartAuction = (gameState, territory) => {
    const order = gameState.playerStates.map((_, offset) => (gameState.currentPlayer + offset) % gameState.playerCount)
        .filter(number => !gameState.playerStates[number].resigned);
    gameState.auction = { territory: CardToData(territory), order, passed: [], highest: null, bids: [] };
    AskBid(gameState, order[0]);
    return `${CardRef(territory)} is up for colonization: each player in turn may bid or pass.`;
};

const AuctionTerritory = (gameState) => CardConverter(gameState.auction.territory);

const AskBid = (gameState, playerNumber) => {
    const { highest, territory } = gameState.auction;
    RequestDecision(gameState, {
        player: gameState.playerStates[playerNumber], type: 'colonyBid', event: territory.name, blocking: true, ui: 'bid', options: null,
        prompt: highest
            ? `Bid more than ${highest.bid} (${gameState.playerStates[highest.player].name}) for ${CardRef(territory)}, or pass`
            : `Bid for ${CardRef(territory)}, or pass`,
        context: { highest },
    });
};

// After a bid or a pass: the next player still in, the winner paying, or no colony at all
const AdvanceAuction = (gameState, lastPlayer) => {
    const auction = gameState.auction;
    const active = auction.order.filter(player => !auction.passed.includes(player));
    if (auction.highest && active.length === 1)
    {
        const winner = gameState.playerStates[auction.highest.player];
        RequestDecision(gameState, {
            player: winner, type: 'colonyPay', event: auction.territory.name, blocking: true, ui: 'sacrifice', options: null,
            prompt: `Sacrifice units (and colonization cards) worth at least ${auction.highest.bid} for ${CardRef(auction.territory)}`,
            context: { bid: auction.highest.bid },
        });
        return;
    }
    if (active.length === 0)
    {
        gameState.auction = null;
        return;
    }
    // The next player round the table who is still in (the highest bidder is not asked to outbid themselves)
    const start = auction.order.indexOf(lastPlayer);
    for (let step = 1; step <= auction.order.length; step++)
    {
        const next = auction.order[(start + step) % auction.order.length];
        if (!auction.passed.includes(next) && next !== auction.highest?.player)
        {
            AskBid(gameState, next);
            return;
        }
    }
};

// ---------------- Colonies

// The territory becomes the player's colony: its lasting bonuses from now on, and what it does at once
const Colonize = (gameState, player, territory) => {
    player.colonies.push(territory);
    const TerritoryEffects = require('../Constants/Territories/TerritoryEffects');
    return TerritoryEffects[territory.name]?.immediate(gameState, player, territory) ?? '';
};

// Free population increases, as many as the yellow bank allows (counting tokens just gained that are not yet in it)
const IncreasePopulationFree = (gameState, player, times, extraTokens = 0) => {
    const grown = Math.max(0, Math.min(times, player.yellowBank + extraTokens));
    for (let i = 0; i < grown; i++)
    {
        player.workerPool++;
        FireTriggers(player, TriggerEvents.PopulationIncrease, { free: true });
    }
    return grown;
};

const DrawMilitaryCards = (gameState, player, count) => gameState.DrawMilitaryCards(player, count);

// Autonomous Territory: 4 science, 5 resources, 6 food or 7 culture
const AskAutonomousChoice = (gameState, player) => RequestDecision(gameState, {
    player, type: 'gainAmount', event: 'Autonomous Territory', prompt: "Choose what the colony gives you",
    options: [['science', 4], ['resources', 5], ['food', 6], ['culture', 7]]
        .map(([what, amount]) => ({ label: `${amount} ${what}`, value: { what, amount } })),
});

module.exports = {
    SacrificeStrength,
    PaymentTotal,
    PaymentBreakdown,
    PaymentProblem,
    MostPayable,
    StartAuction,
    AdvanceAuction,
    AuctionTerritory,
    Colonize,
    IncreasePopulationFree,
    DrawMilitaryCards,
    AskAutonomousChoice,
    IsBonusCard,
    HasJamesCook,
    HasIsabella,
    IsabellaPoints,
};
